import { useEffect, useRef } from "react";
import { Link, type To } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import type { CustomerCatalogueItemSummary } from "@shared-core/data/customer-api-types";
import { Button } from "../../../app/components/primitives/button";
import {
  ProductRender,
  renderKindFor,
  type ProductRenderKind,
} from "../../customer-pages/ProductRender";
import { Check, X } from "lucide-react";

export interface AddedToBasket {
  itemName: string;
  kind: ProductRenderKind;
  monochrome: boolean;
  glossy: boolean;
  /** The chosen options in words, one per line. */
  configurationLines: string[];
  quantity: number;
  /** The line's price, ex VAT; null for a paper our team prices ("Quote on request"). */
  totalPence: number | null;
}

// "Added to your basket": a right-hand sheet on a native <dialog> (showModal gives the focus trap,
// Escape-to-close and an inert page behind it without another dependency), the design Brad approved on the
// merchandising mock. Check out is one click away (B2: there is no "Order now").
export function BasketAddedSheet({
  added,
  crossSell,
  buildPortalUrl,
  onClose,
}: {
  added: AddedToBasket;
  /** Other things to add, under the heading the page gave them; none draws nothing. */
  crossSell: { heading: string; items: CustomerCatalogueItemSummary[] };
  buildPortalUrl: (path: string) => To;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="basket-added-heading"
      onClose={onClose}
      onClick={(event) => {
        // A click on the dialog element itself is a click on the backdrop.
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
      className="fixed top-0 right-0 left-auto m-0 h-dvh max-h-none w-full max-w-md overflow-y-auto border-l bg-background p-0 text-foreground shadow-2xl backdrop:bg-black/40"
    >
      <div className="flex min-h-full flex-col gap-6 p-5">
        <div className="flex items-start justify-between gap-3">
          <h2
            id="basket-added-heading"
            className="flex items-center gap-2 text-lg font-semibold"
          >
            <span className="flex size-6 items-center justify-center rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
              <Check className="size-4" />
            </span>
            Added to your basket
          </h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close"
            onClick={() => dialogRef.current?.close()}
          >
            <X />
          </Button>
        </div>

        <div className="flex gap-4 rounded-xl border p-3">
          <div className="aspect-[4/3] w-24 shrink-0 overflow-hidden rounded-md bg-muted/40">
            <ProductRender
              kind={added.kind}
              monochrome={added.monochrome}
              glossy={added.glossy}
            />
          </div>
          <div className="min-w-0 space-y-0.5 text-sm">
            <p className="font-medium">{added.itemName}</p>
            {added.configurationLines.map((line) => (
              <p key={line} className="text-muted-foreground">
                {line}
              </p>
            ))}
            {added.totalPence === null ? (
              <p className="pt-1 tabular-nums">
                {added.quantity.toLocaleString("en-GB")}:{" "}
                <span className="font-semibold">Quote on request</span>
              </p>
            ) : (
              <p className="pt-1 tabular-nums">
                {added.quantity.toLocaleString("en-GB")} for{" "}
                <span className="font-semibold">
                  {formatQuotedPrice(added.totalPence)}
                </span>{" "}
                <span className="text-muted-foreground">ex VAT</span>
              </p>
            )}
          </div>
        </div>

        {crossSell.items.length > 0 && (
          <section
            aria-labelledby="basket-added-cross-sell"
            className="space-y-3"
          >
            <h3
              id="basket-added-cross-sell"
              className="text-base font-semibold"
            >
              {crossSell.heading}
            </h3>
            <ul className="space-y-2">
              {crossSell.items.map((item) => (
                <li key={item.item_id}>
                  <Link
                    to={buildPortalUrl(
                      paths.portalCatalogueItem({ itemId: item.item_id }),
                    )}
                    onClick={() => dialogRef.current?.close()}
                    className="group flex items-center gap-3 overflow-hidden rounded-xl border bg-card p-2 outline-none transition-shadow hover:border-ring/60 hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    <div className="aspect-[4/3] w-20 shrink-0 overflow-hidden rounded-md bg-muted/40">
                      <ProductRender kind={renderKindFor(item)} />
                    </div>
                    <div className="min-w-0">
                      <p className="leading-snug font-medium group-hover:text-primary">
                        {item.name}
                      </p>
                      <p className="text-sm text-muted-foreground tabular-nums">
                        {item.price
                          ? `From ${formatQuotedPrice(item.price.price_pence)}`
                          : "Price on request"}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-auto space-y-2">
          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={() => dialogRef.current?.close()}
          >
            Continue shopping
          </Button>
          <Button size="lg" className="w-full" asChild>
            <Link to={buildPortalUrl(paths.portalBasket())}>
              Go to checkout
            </Link>
          </Button>
        </div>
      </div>
    </dialog>
  );
}
